import DOMPurify, { type Config as DOMPurifyConfig } from "dompurify";
import type {
  IframeRunInput,
  IframeRunResult,
  IframeRuntime,
  RuntimeMessage,
} from "@/shared/recording-schema";
import { IFRAME_BOOT_SCRIPT } from "./iframeBoot";

export type IframeRuntimeOptions = {
  /** Override sandbox attributes; only "allow-scripts" is on by default. */
  sandboxFlags?: string;
  /** Initial preview theme; defaults to "dark". */
  theme?: RuntimePreviewTheme;
};

const RUNTIME_SOURCE = "code-tape-runtime";
export const RUNTIME_CONSOLE_ARG_LIMIT = 50;
export const RUNTIME_CONSOLE_ARG_MAX_CHARS = 2_000;
export const RUNTIME_PREVIEW_HTML_MAX_CHARS = 200_000;
/** Per-run cap on the combined stdout + stderr line count (including the
 *  reserved truncation-notice slot) — bounds memory and recording size when
 *  async code floods console before the run timeout fires. */
export const RUNTIME_OUTPUT_LINE_LIMIT = 1_000;
export const RUNTIME_OUTPUT_TRUNCATED_NOTICE = "[输出已截断：超过单次运行行数上限]";

const RUNTIME_CSP =
  "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data: blob:; connect-src 'none';";
const REPLAY_PREVIEW_CSP =
  "default-src 'none'; script-src 'none'; style-src 'unsafe-inline'; img-src data: blob:; connect-src 'none';";
const PREVIEW_SANITIZE_CONFIG = {
  WHOLE_DOCUMENT: true,
} satisfies DOMPurifyConfig;

export type RuntimePreviewTheme = "light" | "dark";

/**
 * Theme-aware default styling injected into preview/runtime srcdoc so the empty
 * and rendered sandbox follows the host theme (opaque-origin iframes can't
 * inherit the host's CSS variables). Placed in <head> before user content so a
 * user's own background/color rules still win. Tagged with id="ct-theme" so
 * the runtime boot script can swap it in-place on theme change without losing
 * run state.
 *
 * Split: `color-scheme` lives on `html`, `background`/`color` on `body` only.
 * Per CSS canvas-painting rules, when only `body` has a background, that
 * background propagates to the canvas — so a user `body { background: ... }`
 * fully replaces the iframe canvas paint. If we put a default on `html` too,
 * propagation is suppressed and the user's body bg only paints the body box.
 */
const THEME_HTML_STYLE: Record<RuntimePreviewTheme, string> = {
  light: "color-scheme:light;",
  dark: "color-scheme:dark;",
};

const THEME_BODY_STYLE: Record<RuntimePreviewTheme, string> = {
  light: "background:#f5f5f4;color:#24272d;",
  dark: "background:#1c1f26;color:#e7e9ee;",
};

function themeStyleTag(theme: RuntimePreviewTheme): string {
  // `:where()` gives the default rule zero specificity, so any user
  // background/color rule wins regardless of source order — keeps the issue
  // non-goal "不强制覆盖用户 HTML/CSS 自定义样式" inviolable.
  return (
    `<style id="ct-theme">` +
    `:where(html){${THEME_HTML_STYLE[theme]}}` +
    `:where(body){${THEME_BODY_STYLE[theme]}}` +
    `</style>`
  );
}

type SanitizedPreviewHtml = {
  headHtml: string;
  bodyHtml: string;
};

type RuntimeControlMessage =
  | { type: "init"; runId: string; code: string }
  | { type: "set-theme"; theme: RuntimePreviewTheme };

/**
 * Validate that an incoming postMessage genuinely came from our iframe runtime
 * and matches the current run. Exported so unit tests can poke at the predicate
 * without spinning up a real iframe.
 */
export function acceptRuntimeMessage(
  raw: unknown,
  expected: { runId: string | null; source: MessageEventSource | null },
  event: { source: MessageEventSource | null } = { source: null },
): RuntimeMessage | null {
  if (!raw || typeof raw !== "object") return null;
  const m = raw as { source?: unknown; runId?: unknown; type?: unknown; payload?: unknown };
  if (m.source !== RUNTIME_SOURCE) return null;
  if (typeof m.runId !== "string") return null;
  if (expected.runId && m.runId !== expected.runId) return null;
  if (expected.source && event.source !== expected.source) return null;
  const validTypes = ["ready", "console", "error", "blocked-alert", "complete"];
  if (typeof m.type !== "string" || !validTypes.includes(m.type)) return null;
  if (!m.payload || typeof m.payload !== "object") return null;
  if (!isRuntimePayload(m.type, m.payload)) return null;
  return sanitizeRuntimeMessage(m as RuntimeMessage);
}

function buildSrcDoc(bootScript: string, theme: RuntimePreviewTheme): string {
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${RUNTIME_CSP}" />${themeStyleTag(theme)}<title>code-tape runtime</title></head><body><script>${bootScript}</script></body></html>`;
}

function buildPreviewSrcDoc(previewHtml: string, theme: RuntimePreviewTheme): string {
  const safePreviewHtml = sanitizePreviewHtml(previewHtml);
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${REPLAY_PREVIEW_CSP}" />${themeStyleTag(theme)}<title>code-tape replay preview</title>${safePreviewHtml.headHtml}</head>${safePreviewHtml.bodyHtml}</html>`;
}

function isRuntimePayload(type: string, payload: object): boolean {
  const p = payload as Record<string, unknown>;
  switch (type) {
    case "ready":
      return true;
    case "console":
      return (
        (p.level === "log" || p.level === "warn" || p.level === "error") &&
        Array.isArray(p.args) &&
        p.args.every((arg) => typeof arg === "string")
      );
    case "error":
      return typeof p.message === "string" && (typeof p.stack === "undefined" || typeof p.stack === "string");
    case "blocked-alert":
      return typeof p.message === "string";
    case "complete":
      return typeof p.previewHtml === "string";
    default:
      return false;
  }
}

function isRuntimeControlPortMessage(raw: unknown): boolean {
  if (!raw || typeof raw !== "object") return false;
  const m = raw as { source?: unknown; type?: unknown };
  return m.source === RUNTIME_SOURCE && m.type === "control-port";
}

function limitString(value: string, maxLength: number): string {
  return value.length > maxLength ? value.slice(0, maxLength) : value;
}

function sanitizePreviewHtml(previewHtml: string): SanitizedPreviewHtml {
  const cappedPreviewHtml = limitString(previewHtml, RUNTIME_PREVIEW_HTML_MAX_CHARS);
  if (typeof DOMParser === "undefined") {
    throw new Error("IframeRuntime: DOMParser is required to sanitize preview HTML");
  }
  const sanitizedHtml = DOMPurify.sanitize(cappedPreviewHtml, PREVIEW_SANITIZE_CONFIG);
  const doc = new DOMParser().parseFromString(sanitizedHtml, "text/html");
  return {
    headHtml: limitString(doc.head?.innerHTML ?? "", RUNTIME_PREVIEW_HTML_MAX_CHARS),
    bodyHtml: limitString(doc.body?.outerHTML ?? "<body></body>", RUNTIME_PREVIEW_HTML_MAX_CHARS),
  };
}

function sanitizeRuntimeMessage(message: RuntimeMessage): RuntimeMessage {
  switch (message.type) {
    case "console":
      return {
        ...message,
        payload: {
          ...message.payload,
          args: message.payload.args
            .slice(0, RUNTIME_CONSOLE_ARG_LIMIT)
            .map((arg) => limitString(arg, RUNTIME_CONSOLE_ARG_MAX_CHARS)),
        },
      };
    case "error":
      return {
        ...message,
        payload: {
          message: limitString(message.payload.message, RUNTIME_CONSOLE_ARG_MAX_CHARS),
          ...(message.payload.stack
            ? { stack: limitString(message.payload.stack, RUNTIME_CONSOLE_ARG_MAX_CHARS) }
            : {}),
        },
      };
    case "blocked-alert":
      return {
        ...message,
        payload: { message: limitString(message.payload.message, RUNTIME_CONSOLE_ARG_MAX_CHARS) },
      };
    case "complete":
      return {
        ...message,
        payload: {
          previewHtml: limitString(message.payload.previewHtml, RUNTIME_PREVIEW_HTML_MAX_CHARS),
        },
      };
    case "ready":
      return message;
  }
}

export function createIframeRuntime(options: IframeRuntimeOptions = {}): IframeRuntime {
  let iframe: HTMLIFrameElement | null = null;
  let host: HTMLElement | null = null;
  let messageHandler: ((event: MessageEvent) => void) | null = null;
  let controlPortHandler: ((event: MessageEvent) => void) | null = null;
  let controlPort: MessagePort | null = null;
  let pendingControlMessages: RuntimeControlMessage[] = [];
  let theme: RuntimePreviewTheme = options.theme ?? "dark";
  // Track the current static preview so a theme change can re-render it with the
  // new default background. Null while a JS run iframe is mounted.
  let currentPreviewHtml: string | null = "<body></body>";

  const sandboxFlags = options.sandboxFlags ?? "allow-scripts";

  const teardown = () => {
    if (messageHandler) window.removeEventListener("message", messageHandler);
    messageHandler = null;
    if (controlPortHandler) window.removeEventListener("message", controlPortHandler);
    controlPortHandler = null;
    controlPort?.close();
    controlPort = null;
    pendingControlMessages = [];
    if (iframe && iframe.parentElement) iframe.parentElement.removeChild(iframe);
    iframe = null;
  };

  const createIframe = (sandbox: string, srcdoc: string): Promise<HTMLIFrameElement> => {
    if (!host) throw new Error("IframeRuntime: mount(host) must be called first");
    teardown();
    const el = document.createElement("iframe");
    el.setAttribute("sandbox", sandbox);
    el.setAttribute("title", "code-tape preview");
    el.style.width = "100%";
    el.style.height = "100%";
    el.style.border = "0";
    el.srcdoc = srcdoc;
    iframe = el;
    return new Promise((resolve) => {
      el.addEventListener("load", () => resolve(el), { once: true });
      host!.appendChild(el);
    });
  };

  const createRuntimeIframe = (sandbox: string, srcdoc: string): Promise<HTMLIFrameElement> => {
    if (!host) throw new Error("IframeRuntime: mount(host) must be called first");
    const targetHost = host;
    teardown();
    const el = document.createElement("iframe");
    el.setAttribute("sandbox", sandbox);
    el.setAttribute("title", "code-tape preview");
    el.style.width = "100%";
    el.style.height = "100%";
    el.style.border = "0";
    el.srcdoc = srcdoc;
    iframe = el;
    return new Promise((resolve) => {
      controlPortHandler = (event: MessageEvent) => {
        if (event.source !== el.contentWindow) return;
        if (!isRuntimeControlPortMessage(event.data)) return;
        const [nextPort] = event.ports;
        if (!nextPort) return;
        controlPort = nextPort;
        for (const message of pendingControlMessages) controlPort.postMessage(message);
        pendingControlMessages = [];
        if (controlPortHandler) window.removeEventListener("message", controlPortHandler);
        controlPortHandler = null;
      };
      window.addEventListener("message", controlPortHandler);
      el.addEventListener(
        "load",
        () => {
          resolve(el);
        },
        { once: true },
      );
      targetHost.appendChild(el);
    });
  };

  const postRuntimeControlMessage = (message: RuntimeControlMessage) => {
    if (controlPort) controlPort.postMessage(message);
    else pendingControlMessages.push(message);
  };

  return {
    async mount(target: HTMLElement) {
      host = target;
      if (!iframe) await createIframe("", buildPreviewSrcDoc("<body></body>", theme));
    },
    setTheme(nextTheme: RuntimePreviewTheme) {
      if (nextTheme === theme) return;
      theme = nextTheme;
      // For a static preview, re-render with the new theme; for a JS run iframe
      // (currentPreviewHtml === null), postMessage so the boot script swaps the
      // injected #ct-theme style in place — preserves run state and DOM mutations.
      if (!host) return;
      if (currentPreviewHtml !== null) {
        void createIframe("", buildPreviewSrcDoc(currentPreviewHtml, theme));
      } else {
        postRuntimeControlMessage({ type: "set-theme", theme });
      }
    },
    async run(input: IframeRunInput): Promise<IframeRunResult> {
      currentPreviewHtml = null;
      const frame = await createRuntimeIframe(sandboxFlags, buildSrcDoc(IFRAME_BOOT_SCRIPT, theme));
      const stdout: string[] = [];
      const stderr: string[] = [];
      let outputTruncated = false;
      // Shared cap over stdout + stderr (a runaway async task can flood either
      // stream). The final slot is reserved for the truncation notice so the
      // combined line count never exceeds RUNTIME_OUTPUT_LINE_LIMIT.
      const pushOutput = (sink: string[], line: string) => {
        if (outputTruncated) return;
        const total = stdout.length + stderr.length;
        if (total >= RUNTIME_OUTPUT_LINE_LIMIT - 1) {
          outputTruncated = true;
          stderr.push(RUNTIME_OUTPUT_TRUNCATED_NOTICE);
          return;
        }
        sink.push(line);
      };

      return new Promise<IframeRunResult>((resolve) => {
        let settled = false;
        const finish = (result: IframeRunResult) => {
          if (settled) return;
          settled = true;
          if (messageHandler) window.removeEventListener("message", messageHandler);
          messageHandler = null;
          clearTimeout(timer);
          resolve(result);
        };

        const timer = setTimeout(() => {
          // Destroy the run iframe so a runaway async task (setInterval, fetch
          // retry loop, unbounded DOM growth, etc.) stops consuming CPU/memory
          // once we give up — matches the 技术方案 timeout contract ("销毁 iframe").
          if (iframe === frame) teardown();
          finish({ runId: input.runId, status: "timeout", stdout, stderr });
        }, input.timeoutMs);

        messageHandler = (event: MessageEvent) => {
          const msg = acceptRuntimeMessage(
            event.data,
            { runId: input.runId, source: frame.contentWindow },
            { source: event.source },
          );
          if (!msg) return;
          switch (msg.type) {
            case "console":
              if (msg.payload.level === "error") pushOutput(stderr, msg.payload.args.join(" "));
              else pushOutput(stdout, msg.payload.args.join(" "));
              break;
            case "error":
              finish({
                runId: input.runId,
                status: "error",
                phase: "runtime",
                message: msg.payload.message,
                stack: msg.payload.stack,
                stdout,
                stderr,
              });
              break;
            case "blocked-alert":
              pushOutput(stderr, `[blocked] ${msg.payload.message}`);
              break;
            case "complete":
              finish({
                runId: input.runId,
                status: "complete",
                previewHtml: msg.payload.previewHtml,
                stdout,
                stderr,
              });
              break;
            case "ready":
              break;
          }
        };
        window.addEventListener("message", messageHandler);
        postRuntimeControlMessage({ type: "init", runId: input.runId, code: input.compiledCode });
      });
    },
    async renderPreview(previewHtml: string): Promise<void> {
      currentPreviewHtml = previewHtml;
      await createIframe("", buildPreviewSrcDoc(previewHtml, theme));
    },
    async renderDocument(html: string): Promise<string> {
      currentPreviewHtml = html;
      const safe = sanitizePreviewHtml(html);
      await createIframe("", buildPreviewSrcDoc(html, theme));
      // Return the SANITIZED markup actually rendered (scripts stripped), so the
      // persisted previewHtml is script-free regardless of who re-renders it.
      const sanitized = safe.headHtml ? `${safe.headHtml}${safe.bodyHtml}` : safe.bodyHtml;
      return limitString(sanitized, RUNTIME_PREVIEW_HTML_MAX_CHARS);
    },
    reset() {
      currentPreviewHtml = "<body></body>";
      teardown();
    },
    destroy() {
      currentPreviewHtml = null;
      teardown();
      host = null;
    },
  };
}
